import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Button } from "./ui/button";
import { Loader2, CheckCircle2, UploadCloud } from "lucide-react";
import { SECTOR_KEYS } from "../lib/brand";
import { useT } from "../lib/i18n";
import api, { formatApiError } from "../lib/api";

const EMPTY = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  sector: "",
  cover_letter: "",
};

export default function ApplicationDialog({ open, onOpenChange, defaultSector, job }) {
  const { t } = useT();
  const [form, setForm] = useState({ ...EMPTY, sector: defaultSector || "" });
  const [cv, setCv] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      setForm((f) => ({ ...f, sector: defaultSector || f.sector || "" }));
    }
  }, [open, defaultSector]);

  const reset = () => {
    setForm({ ...EMPTY, sector: defaultSector || "" });
    setCv(null);
    setSuccess(false);
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== "application/pdf") {
      toast.error(t("form.error.pdf"));
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error(t("form.error.size"));
      return;
    }
    setCv(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!cv) {
      toast.error(t("form.error.cv"));
      return;
    }
    if (!form.sector) {
      toast.error(t("form.error.sector"));
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (job?.id) fd.append("job_id", job.id);
      fd.append("cv", cv);
      await api.post("/applications", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSuccess(true);
      toast.success(t("form.success.toast"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, t("form.error.submit")));
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (o) => {
    onOpenChange(o);
    if (!o) setTimeout(reset, 250);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent
        className="max-w-2xl max-h-[92vh] overflow-y-auto"
        data-testid="application-dialog"
      >
        {success ? (
          <div className="py-10 flex flex-col items-center text-center" data-testid="application-success">
            <div className="h-16 w-16 rounded-full bg-green-50 grid place-items-center mb-4">
              <CheckCircle2 className="text-green-600" size={34} />
            </div>
            <h3 className="font-display text-2xl font-bold text-navy mb-2">
              {t("form.success.title")}
            </h3>
            <p className="text-[var(--text-secondary)] max-w-md">{t("form.success.text")}</p>
            <Button
              className="mt-6 btn-navy"
              onClick={() => handleOpen(false)}
              data-testid="application-success-close"
            >
              {t("form.close")}
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-navy">
                {job ? `${t("form.application.titleJob")} ${job.title}` : t("form.application.title")}
              </DialogTitle>
              <DialogDescription>{t("form.application.subtitle")}</DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
              <div>
                <Label htmlFor="first_name">{t("form.firstName")} *</Label>
                <Input
                  id="first_name"
                  required
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  data-testid="application-first-name"
                />
              </div>
              <div>
                <Label htmlFor="last_name">{t("form.lastName")} *</Label>
                <Input
                  id="last_name"
                  required
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  data-testid="application-last-name"
                />
              </div>
              <div>
                <Label htmlFor="email">{t("form.email")} *</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="application-email"
                />
              </div>
              <div>
                <Label htmlFor="phone">{t("form.phone")} *</Label>
                <Input
                  id="phone"
                  required
                  placeholder="514-000-0000"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="application-phone"
                />
              </div>
              <div className="md:col-span-2">
                <Label>{t("form.sector")} *</Label>
                <Select
                  value={form.sector}
                  onValueChange={(v) => setForm({ ...form, sector: v })}
                >
                  <SelectTrigger data-testid="application-sector-trigger">
                    <SelectValue placeholder={t("form.sector.choose")} />
                  </SelectTrigger>
                  <SelectContent>
                    {SECTOR_KEYS.map((k) => (
                      <SelectItem key={k} value={k} data-testid={`sector-option-${k}`}>
                        {t(`sectors.${k}.label`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="cover_letter">{t("form.coverLetter")} *</Label>
                <Textarea
                  id="cover_letter"
                  required
                  rows={5}
                  placeholder={t("form.coverLetter.placeholder")}
                  value={form.cover_letter}
                  onChange={(e) => setForm({ ...form, cover_letter: e.target.value })}
                  data-testid="application-cover-letter"
                />
              </div>
              <div className="md:col-span-2">
                <Label>{t("form.cv")} *</Label>
                <label
                  htmlFor="cv-upload"
                  className="mt-1 flex items-center justify-center gap-3 border-2 border-dashed border-[var(--border)] rounded-sm px-4 py-6 cursor-pointer hover:border-gold transition"
                  data-testid="application-cv-dropzone"
                >
                  <UploadCloud className="text-navy" size={22} />
                  <span className="text-sm text-[var(--text-secondary)]">
                    {cv ? cv.name : t("form.cv.hint")}
                  </span>
                </label>
                <input
                  id="cv-upload"
                  type="file"
                  accept="application/pdf"
                  onChange={handleFile}
                  className="hidden"
                  data-testid="application-cv-input"
                />
              </div>

              <DialogFooter className="md:col-span-2 mt-2">
                <Button type="button" variant="outline" onClick={() => handleOpen(false)} data-testid="application-cancel-btn">
                  {t("form.cancel")}
                </Button>
                <Button type="submit" disabled={loading} className="btn-gold" data-testid="application-submit-btn">
                  {loading && <Loader2 className="animate-spin mr-2" size={16} />}
                  {t("form.submit")}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
